import type { Agent, Config, LspStatus, Path, Project, VcsInfo } from "@mux/sdk/v2/client"
import type { ServerConnection } from "@/context/server"
import { jsonArray, jsonObject, jsonString } from "@/transport/decode"
import { expectJson } from "@/transport/http"
import { serverFetch } from "@/transport/server"

/** A prompt command the runtime expands when a prompt starts with `/name`. */
export type SlashCommandInfo = { name: string; description: string }

export type DirectoryBootstrap = {
  project: Project
  path: Path
  config: Config
  agents: Agent[]
  commands: SlashCommandInfo[]
  lsp: LspStatus[]
  vcs?: VcsInfo
}

function decodeSlashCommand(value: unknown): SlashCommandInfo {
  const item = jsonObject(value, "Server returned an invalid command")
  return {
    name: jsonString(item.name, "Server returned an invalid command"),
    description: jsonString(item.description, "Server returned an invalid command"),
  }
}

function decodeDirectoryBootstrap(value: unknown): DirectoryBootstrap {
  const root = jsonObject(value, "Server returned invalid directory bootstrap")
  return {
    project: jsonObject(root.project, "Server returned invalid project") as Project,
    path: jsonObject(root.path, "Server returned invalid path") as Path,
    config: jsonObject(root.config, "Server returned invalid config") as Config,
    agents: jsonArray(root.agents, "Server returned invalid agents") as Agent[],
    commands: jsonArray(root.commands, "Server returned invalid commands").map(decodeSlashCommand),
    lsp: jsonArray(root.lsp, "Server returned invalid LSP state") as LspStatus[],
    vcs: root.vcs == null ? undefined : jsonObject(root.vcs, "Server returned invalid VCS state") as VcsInfo,
  }
}

export async function getDirectoryBootstrap(server: ServerConnection.HttpBase, directory: string, signal?: AbortSignal) {
  return expectJson(
    await serverFetch(server, "/project/bootstrap", { signal, headers: { "X-Mux-Directory": directory } }),
    decodeDirectoryBootstrap,
    { error: "Directory bootstrap request failed" },
  )
}
